import { AlertTriangle, Clock, PowerOff, Search } from "lucide-react";
import { api } from "../../lib/api";
import { urutAZ } from "../../lib/urutAZ";

/**
 * Baris filter produk BERSAMA — Master Produk dan Price List (brief owner 22 Sep
 * 2026 G.1: "Buatkan tab filter sama persis dengan yang ada pada Master Produk").
 *
 * SATU komponen, bukan salinan: dua salinan baris filter pasti berpisah jalan
 * (riwayat pemilih template yang dulu tersalin di 6 berkas, CLAUDE.md). Backend-nya
 * juga satu penyaring (`products.py::query_saring_produk`), jadi pilihan yang sama
 * menghasilkan himpunan barang yang sama di kedua layar.
 *
 * Komponen ini hanya MENGGAMBAR kontrol filter (fragment) — pembungkus, tombol
 * urut, dan filter khas tiap layar (Price List: cabang, "belum ada harga", rinci,
 * Mode Daftar Harga) dirender pemanggil di sekitarnya / lewat `children`.
 * data-testid dikirim pemanggil (`testId`) supaya id lama kedua layar tidak berubah.
 */

// Kunci React Query opsi filter — dipakai Master Produk, Price List, dan prefetch
// menu (Layout.jsx) supaya ketiganya berbagi cache yang SAMA.
export const OPSI_QK = ["inventory-master-opsi"];

// Fallback tampilan sebelum `GET /inventory/master-opsi` menjawab — label UI
// murni, bukan aturan akun (itu tetap backend `master_opsi.py`).
export const KATEGORI_UTAMA_FALLBACK = [
  { key: "handphone", label: "Handphone", wajib_status: true },
  { key: "tab", label: "Tab", wajib_status: true },
  { key: "laptop", label: "Laptop", wajib_status: true },
  { key: "wearable", label: "Wearable", wajib_status: true },
  { key: "original_acc", label: "Original Acc", wajib_status: false },
  { key: "accessories", label: "Accessories", wajib_status: false },
  { key: "sim_sticker", label: "SIM Sticker", wajib_status: false },
];

/** Opsi filter + gudang + supplier dalam SATU fetch (satu kunci cache).
 *  Gudang HANYA diminta bila `bolehGudang` (izin `inventory_gudang.view`) — tanpa
 *  itu backend menjawab 403 yang dulu ditelan diam-diam dan tercatat sebagai
 *  galat konsol di setiap pembukaan Master Produk (KEPUTUSAN-TERTUNDA E30).
 *  Supplier cukup `inventory_produk.view` (`supplier_ledger._izin_baca_daftar_supplier`).
 *  Kegagalan gudang/supplier DICATAT (`gudangGagal`/`supplierGagal`) supaya
 *  dropdown yang hilang bisa dibedakan dari "memang belum ada datanya". */
export async function ambilOpsiFilterProduk(bolehGudang) {
  const [o, g, s] = await Promise.all([
    api.get("/inventory/master-opsi"),
    bolehGudang ? api.get("/inventory/warehouses").catch(() => null) : Promise.resolve(undefined),
    api.get("/supplier").catch(() => null),
  ]);
  return {
    opsi: o.data, gudang: g?.data?.items || [], suppliers: s?.data?.items || [],
    gudangGagal: g === null, supplierGagal: s === null,
  };
}

export function daftarKategoriUtama(opsi) {
  return opsi?.kategori_utama?.length ? opsi.kategori_utama : KATEGORI_UTAMA_FALLBACK;
}

/** Opsi kategori RATA ("Handphone - New"/"Handphone - Second" per kategori gadget,
 *  satu entri untuk sisanya) — urutan mengikuti registry (nomor akun), BUKAN A-Z.
 *  Nilainya kunci kanonik; backend ikut menyaring kunci alias lama. */
export function opsiKategoriRata(kategoriUtamaList) {
  return (kategoriUtamaList || []).flatMap(k => k.wajib_status
    ? [{ v: `${k.key}_baru`, l: `${k.label} - New` }, { v: `${k.key}_second`, l: `${k.label} - Second` }]
    : [{ v: k.key, l: k.label }]);
}

/** SATU daftar brand lintas kategori (union Gadget+Accessories, jatuh ke daftar
 *  `brand` lama kalau keduanya kosong) — "urutan ketik admin", tidak diurut. */
export function daftarBrandFilter(opsi) {
  const gab = Array.from(new Set([...(opsi?.brand_gadget || []), ...(opsi?.brand_accessories || [])]));
  return gab.length ? gab : (opsi?.brand || []);
}

export const FILTER_PRODUK_KOSONG = {
  kategori: "", sub_kategori: "", brand: "", warehouse_id: "", supplier: "",
  stok: "", only_aging: false, include_inactive: false,
};

const TESTID_MASTER_PRODUK = {
  cari: "inv-prod-search", kategori: "inv-prod-kategori", subKategori: "inv-prod-subkategori",
  brand: "inv-prod-fbrand", gudang: "inv-prod-fgudang", stok: "inv-prod-fstok",
  supplier: "inv-prod-fsupplier", gagal: "inv-prod-filter-gagal", aging: "inv-prod-faging",
  nonaktif: "inv-prod-finactive",
};

const sel = "h-9 px-2 border border-slate-200 rounded-lg text-sm bg-white";

export default function FilterProduk({
  q, onQ, onQEnter, nilai, onUbah, opsi, gudang = [], suppliers = [],
  gudangGagal = false, supplierGagal = false, agingHari, testId = TESTID_MASTER_PRODUK,
  keteranganStok, children,
}) {
  const kategoriRata = opsiKategoriRata(daftarKategoriUtama(opsi));
  const brandList = daftarBrandFilter(opsi);
  const kategoriCustom = opsi?.kategori_custom || [];
  const id = { ...TESTID_MASTER_PRODUK, ...testId };
  return (
    <>
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={e => onQ(e.target.value)}
               onKeyDown={e => { if (e.key === "Enter" && onQEnter) onQEnter(); }}
               placeholder="Cari SKU / nama / barcode" data-testid={id.cari}
               aria-label="Cari SKU, nama, atau barcode produk"
               className="h-9 pl-9 pr-3 border border-slate-200 rounded-lg text-sm w-60 bg-white" />
      </div>
      {/* Kategori AKUNTANSI — urutan mengikuti nomor akun COA, bukan abjad. */}
      <select value={nilai.kategori} onChange={e => onUbah({ kategori: e.target.value })}
              data-testid={id.kategori} aria-label="Filter kategori akuntansi" className={sel}>
        <option value="">Semua kategori</option>
        {kategoriRata.map(k => <option key={k.v} value={k.v}>{k.l}</option>)}
      </select>
      {kategoriCustom.length > 0 && (
        <select value={nilai.sub_kategori} onChange={e => onUbah({ sub_kategori: e.target.value })}
                data-testid={id.subKategori} aria-label="Filter kategori produk" className={sel}>
          <option value="">Semua kategori produk</option>
          {urutAZ(kategoriCustom).map(k => <option key={k} value={k}>{k}</option>)}
        </select>
      )}
      {brandList.length > 0 && (
        <select value={nilai.brand} onChange={e => onUbah({ brand: e.target.value })}
                data-testid={id.brand} aria-label="Filter brand" className={sel}>
          <option value="">Semua brand</option>
          {brandList.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
      )}
      {/* Gudang/supplier diurut ulang di klien (localeCompare "id") — urutan
          server case-sensitive menaruh "Zebra" sebelum "apple". */}
      {gudang.length > 0 && (
        <select value={nilai.warehouse_id} onChange={e => onUbah({ warehouse_id: e.target.value })}
                data-testid={id.gudang} aria-label="Filter gudang" className={sel}>
          <option value="">Semua gudang</option>
          {[...gudang].sort((a, b) => (a.nama || "").localeCompare(b.nama || "", "id"))
            .map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
        </select>
      )}
      {/* Disaring di SERVER supaya total & paginasi ikut — menyaring halaman yang
          sudah terpotong di klien menghasilkan daftar yang berbohong. */}
      <select value={nilai.stok} onChange={e => onUbah({ stok: e.target.value })}
              data-testid={id.stok} aria-label="Filter stok" className={sel}
              title={keteranganStok}>
        <option value="">Semua stok</option>
        <option value="ada">Ada stok (&gt; 0)</option>
        <option value="kosong">Stok kosong (= 0)</option>
        <option value="minus">Stok minus (&lt; 0)</option>
      </select>
      {suppliers.length > 0 && (
        <select value={nilai.supplier} onChange={e => onUbah({ supplier: e.target.value })}
                data-testid={id.supplier} aria-label="Filter supplier" className={sel}>
          <option value="">Semua supplier</option>
          {[...suppliers].sort((a, b) => (a.nama || "").localeCompare(b.nama || "", "id"))
            .map(s => <option key={s.id} value={s.nama}>{s.nama}</option>)}
        </select>
      )}
      {(gudangGagal || supplierGagal) && (
        <span data-testid={id.gagal}
              title="Daftar ini dimuat terpisah dari daftar produk; produknya sendiri tetap tampil."
              className="h-9 px-2 inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 text-[11px] text-amber-800">
          <AlertTriangle className="w-3.5 h-3.5" />
          Filter {[gudangGagal && "gudang", supplierGagal && "supplier"].filter(Boolean).join(" & ")} gagal dimuat
        </span>
      )}
      <button type="button" onClick={() => onUbah({ only_aging: !nilai.only_aging })} data-testid={id.aging}
              title={`Barang yang nangkring >= ${agingHari ?? 60} hari (atur ambangnya di Pengaturan Master Produk)`}
              className={`h-9 px-3 rounded-lg border text-xs font-medium inline-flex items-center gap-1.5 ${
                nilai.only_aging ? "border-rose-300 bg-rose-50 text-rose-700" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
        <Clock className="w-3.5 h-3.5" /> Stok lama
      </button>
      <button type="button" onClick={() => onUbah({ include_inactive: !nilai.include_inactive })}
              data-testid={id.nonaktif}
              title="Tampilkan juga produk yang sudah dinonaktifkan"
              className={`h-9 px-3 rounded-lg border text-xs font-medium inline-flex items-center gap-1.5 ${
                nilai.include_inactive ? "border-slate-400 bg-slate-100 text-slate-700" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
        <PowerOff className="w-3.5 h-3.5" /> Termasuk nonaktif
      </button>
      {children}
    </>
  );
}
